/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Incident & Operational-Period family
 *  (operator-cop-doctrine §3A.7 / §8.4; SITL incident-period UI).
 *
 *  Single source of the badge palette for the operator-visible incident
 *  management surface (IncidentManagementSection): the incident hazard_type
 *  badge, the incident open/closed status pill, and the operational-period
 *  planned/active/closed status pill.
 *
 *  This is a DISTINCT encoding domain from the three map hazard families
 *  (responseLevel / fireStages / capHazards): those colour map markers by
 *  urgency-within-family; this colours an incident's administrative
 *  classification + lifecycle in the side-panel record surface. Per §3A.7 the
 *  asset / aircraft / lifecycle families keep their own modules and any hue
 *  overlap is "a different encoding domain, not the blue/amber debt" — so this
 *  module owns its own hex constants and pairs every colour with an icon +
 *  doctrinal label (hue never load-bearing alone).
 *
 *  The hazard icons mirror the §3B.2 layer-group emoji (🔥 / 🌊 / 🧊) so the
 *  operator reads the same iconography in the panel as in the layer panel.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates / ./sectorStyles /
 *     ./outageStyles / ./aircraftRoles / ./floodGauges / ./roadClosureStyles
 *     (enforced by the no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

import type {
  IncidentHazardType,
  IncidentLifecycleState,
  IncidentStatus,
  OperationalPeriodStatus,
} from '@tsr/shared-types';

export interface BadgeStyle {
  /** CSS hex for the badge text + tinted background. */
  color: string;
  /** Short doctrinal label, shown in the badge (colour never alone). */
  label: string;
  /** Emoji/glyph paired with the colour so hue is not load-bearing. */
  icon: string;
}

/** Incident hazard_type → badge (icons mirror the §3B.2 layer-group emoji). */
const HAZARD_STYLES: Record<IncidentHazardType, BadgeStyle> = {
  wildfire: { color: '#f97316', label: 'Wildfire', icon: '🔥' },
  flood: { color: '#3b82f6', label: 'Flood', icon: '🌊' },
  ice_storm: { color: '#7dd3fc', label: 'Ice Storm', icon: '🧊' },
  cap_warning: { color: '#f59e0b', label: 'CAP Warning', icon: '⚠️' },
  multi_hazard: { color: '#a855f7', label: 'Multi-Hazard', icon: '🛑' },
  other: { color: '#94a3b8', label: 'Other', icon: '◆' },
};

const UNKNOWN_HAZARD: BadgeStyle = { color: '#94a3b8', label: 'Unknown', icon: '◆' };

/** Incident open/closed lifecycle → pill. Open reads "live" (calm emerald, no
 *  motion); closed is ambient grey. */
const INCIDENT_STATUS_STYLES: Record<IncidentStatus, BadgeStyle> = {
  open: { color: '#34d399', label: 'Open', icon: '●' },
  closed: { color: '#64748b', label: 'Closed', icon: '✓' },
};

/** R1/#477 incident lifecycle phase → pill (panel surface). Distinct hue AND icon
 *  per phase (hue never load-bearing alone); the progression brightens toward response,
 *  then greys at deactivated. ORTHOGONAL to the open/closed status pill. Vocabulary
 *  doctrine-locked to Ontario IMS Guidance 2.0 (docs/ontario-ims-doctrine-reference.md;
 *  In Response = App A #68 "Response"; Deactivated = §8 deactivation). */
const LIFECYCLE_STATE_STYLES: Record<IncidentLifecycleState, BadgeStyle> = {
  detected: { color: '#94a3b8', label: 'Detected', icon: '○' },
  confirmed: { color: '#fbbf24', label: 'Confirmed', icon: '◉' },
  owned: { color: '#38bdf8', label: 'Owned', icon: '◈' },
  response: { color: '#34d399', label: 'In Response', icon: '◆' },
  deactivated: { color: '#64748b', label: 'Deactivated', icon: '✓' },
};

/** Operational-period planned/active/closed → pill. Echoes the LifecycleStepper
 *  grammar (planned = slate/future, active = sky/current, closed = grey). */
const PERIOD_STATUS_STYLES: Record<OperationalPeriodStatus, BadgeStyle> = {
  planned: { color: '#94a3b8', label: 'Planned', icon: '○' },
  active: { color: '#38bdf8', label: 'Active', icon: '●' },
  closed: { color: '#64748b', label: 'Closed', icon: '✓' },
};

function isHazard(v: unknown): v is IncidentHazardType {
  return typeof v === 'string' && v in HAZARD_STYLES;
}

/** Resolve the badge for an incident hazard_type (tolerant of unknown values —
 *  an unmapped non-empty string keeps its raw text rather than dropping). */
export function hazardStyle(hazard: string | null | undefined): BadgeStyle {
  if (isHazard(hazard)) return HAZARD_STYLES[hazard];
  if (typeof hazard === 'string' && hazard.length > 0) {
    return { color: UNKNOWN_HAZARD.color, label: hazard.replace(/_/g, ' '), icon: UNKNOWN_HAZARD.icon };
  }
  return UNKNOWN_HAZARD;
}

/** Resolve the pill for an incident open/closed status. */
export function incidentStatusStyle(status: string | null | undefined): BadgeStyle {
  if (typeof status === 'string' && status in INCIDENT_STATUS_STYLES) {
    return INCIDENT_STATUS_STYLES[status as IncidentStatus];
  }
  return UNKNOWN_HAZARD;
}

/** Resolve the pill for an incident lifecycle phase (R1/#477). */
export function incidentLifecycleStyle(state: string | null | undefined): BadgeStyle {
  if (typeof state === 'string' && state in LIFECYCLE_STATE_STYLES) {
    return LIFECYCLE_STATE_STYLES[state as IncidentLifecycleState];
  }
  return UNKNOWN_HAZARD;
}

/** Resolve the pill for an operational-period planned/active/closed status. */
export function periodStatusStyle(status: string | null | undefined): BadgeStyle {
  if (typeof status === 'string' && status in PERIOD_STATUS_STYLES) {
    return PERIOD_STATUS_STYLES[status as OperationalPeriodStatus];
  }
  return UNKNOWN_HAZARD;
}
